import React, { createContext, forwardRef, useImperativeHandle, useEffect, useState, useCallback } from 'react';
import { fetchCommonAppData } from '@udemy/ud-api';
import { SubscriptionPlanProductType, SubscriptionStatus, useSubscriptionPlansQuery, useCurrentSubscriptionTypeQuery, useGetAllAvailableConsumerSubscriptionPlansQuery } from '../graphql/api-platform-graphql';
import { TEAM_PLAN_V1_FALLBACK_RESPONSE, CONSUMER_PRODUCT_TYPES } from './constants';
const ACTIVE_SUBSCRIPTION_STATUSES = [SubscriptionStatus.Trial, SubscriptionStatus.Active, SubscriptionStatus.Canceled];

/**
 * Subscription plan data from GraphQL, including subscriber state.
 * This represents the cached GraphQL response with metadata about the user's subscription status.
 */

const LOADING_STATE = {
  isLoading: true
};

/**
 * Context for GraphQL subscription plan data, keyed by product type
 */

const defaultGraphQLSubscriptionPlansContext = {
  CONSUMERSUBSCRIPTION: LOADING_STATE,
  CONSUMERSUBSCRIPTION_VALUE_PLAN: LOADING_STATE,
  ENTERPRISE: LOADING_STATE,
  ENTERPRISE_AI_GROWTH: LOADING_STATE,
  ENTERPRISE_AI_READINESS: LOADING_STATE,
  ENTERPRISE_BASE: LOADING_STATE,
  ENTERPRISEPRO: LOADING_STATE,
  TEAM: LOADING_STATE,
  UDEMYPRO: LOADING_STATE,
  GROW_WITH_GOOGLE: LOADING_STATE,
  CONSUMERSUBSCRIPTION_PLUS_GROW_WITH_GOOGLE: LOADING_STATE
};
export const GraphQLSubscriptionPlansContext = /*#__PURE__*/createContext(defaultGraphQLSubscriptionPlansContext);

/**
 * Props for the GraphQLSubscriptionPlanProvider component
 */

/**
 * Fetches all consumer subscription plans the user has available to purchase to them.
 * Differs from fetchGraphQLSubscriptionPlan in that it fetches all consumer plans instead of any given single plan,
 * and the GQL query backing this function does not return nothing if the user is subscribed to a plan,
 * unlike fetchGraphQLSubscriptionPlan which will return nothing if the user is subscribed to a plan.
 */
async function fetchGraphQLUpgradeableConsumerSubscriptionPlans(queryVariables) {
  const allConsumerPlans = (await useGetAllAvailableConsumerSubscriptionPlansQuery.fetcher(queryVariables)()).subscriptionPlanUpgradesByProductFamily;
  return allConsumerPlans.map(graphQLPlan => ({
    subscriptionPlan: graphQLPlan,
    isSubscriberToSpecificPlan: false,
    isLoading: false
  }));
}

/**
 * Fetches a single subscription plan from GraphQL without any adaptation.
 * Returns GraphQL data that can be adapted later with different formatters.
 *
 * Try not to use this for a consumer subscription plan, as you can get them all with fetchGraphQLUpgradeableConsumerSubscriptionPlans
 * instead.
 *
 * This is kept around in case we need it to retrieve a non-consumer plan.
 */
async function fetchGraphQLSubscriptionPlan(queryVariables) {
  const fetchSubscriptionPlansQuery = useSubscriptionPlansQuery.fetcher(queryVariables)().then(data => {
    var _queryVariables$filte;
    const subscriptionPlans = data === null || data === void 0 ? void 0 : data.subscriptionPlans;

    // HACK HACK HACK - this is a hardcoded fallback to support countries with TPv1 by displaying a hardcoded response
    // with a 14-day trial and a price of $360 USD per year (rather than local currency).
    // This is a temporary solution until we drop support for TPv1.
    // In cases where TPv2 data is returned via GraphQL, this should never be used.
    if ((subscriptionPlans === null || subscriptionPlans === void 0 ? void 0 : subscriptionPlans.length) === 0 && ((_queryVariables$filte = queryVariables.filters) === null || _queryVariables$filte === void 0 ? void 0 : _queryVariables$filte.productType) === SubscriptionPlanProductType.Team) {
      return TEAM_PLAN_V1_FALLBACK_RESPONSE;
    }

    // This solution assumes one subscription plan per product type
    return subscriptionPlans === null || subscriptionPlans === void 0 ? void 0 : subscriptionPlans.find(subscriptionPlan => {
      var _queryVariables$filte2;
      return subscriptionPlan.productType == ((_queryVariables$filte2 = queryVariables.filters) === null || _queryVariables$filte2 === void 0 ? void 0 : _queryVariables$filte2.productType);
    });
  });
  const [subscriptionPlan] = await Promise.all([fetchSubscriptionPlansQuery]);
  if (!subscriptionPlan) {
    return undefined;
  }
  return {
    subscriptionPlan,
    isSubscriberToSpecificPlan: false,
    isLoading: false
  };
}

/**
 * GraphQLSubscriptionPlanProvider
 *
 * Internal provider that handles raw GraphQL data fetching and caching.
 * This provider does NOT perform any currency formatting or adaptation.
 * It simply fetches and caches raw subscription plan data from the GraphQL API.
 */
export const GraphQLSubscriptionPlanProvider = /*#__PURE__*/forwardRef((props, ref) => {
  // State to store GraphQL subscription plan data
  const [graphqlPlansContext, setGraphQLPlansContext] = useState(defaultGraphQLSubscriptionPlansContext);
  const removeLoadingStates = useCallback(() => {
    setGraphQLPlansContext(currentContext => {
      const newContext = {
        ...currentContext
      };
      for (const [productType, plan] of Object.entries(newContext)) {
        if (plan !== null && plan !== void 0 && plan.isLoading) {
          newContext[productType] = undefined;
        }
      }
      return newContext;
    });
  }, [setGraphQLPlansContext]);

  // Fetch subscription plans from GraphQL
  const fetchGraphQLPlans = React.useCallback(variables => {
    (async () => {
      const isSubscriberToAnyConsumerPlan = await fetchCommonAppData({
        header: true
      }).then(context => {
        const {
          user
        } = context.data.header;
        if ('consumer_subscription_active' in user) {
          return !!user.consumer_subscription_active;
        }
        return false;
      });
      const getSubscribedPlanProductType = async () => {
        return useCurrentSubscriptionTypeQuery.fetcher()().then(data => {
          var _currentSubscriptionE;
          const currentSubscriptionEnrollment = data === null || data === void 0 ? void 0 : data.currentSubscriptionEnrollment;
          if (currentSubscriptionEnrollment !== null && currentSubscriptionEnrollment !== void 0 && (_currentSubscriptionE = currentSubscriptionEnrollment.subscribedPlan) !== null && _currentSubscriptionE !== void 0 && _currentSubscriptionE.productType && CONSUMER_PRODUCT_TYPES.has(currentSubscriptionEnrollment.subscribedPlan.productType) && ACTIVE_SUBSCRIPTION_STATUSES.includes(currentSubscriptionEnrollment === null || currentSubscriptionEnrollment === void 0 ? void 0 : currentSubscriptionEnrollment.status)) {
            var _currentSubscriptionE2;
            return currentSubscriptionEnrollment === null || currentSubscriptionEnrollment === void 0 || (_currentSubscriptionE2 = currentSubscriptionEnrollment.subscribedPlan) === null || _currentSubscriptionE2 === void 0 ? void 0 : _currentSubscriptionE2.productType;
          } else {
            return undefined;
          }
        }).catch(() => {
          // We might still have other data about the subs plan, so we want to fail closed here
          // and allow the other subs data to be returned.
          return undefined;
        });
      };
      const subscribedPlanProductType = isSubscriberToAnyConsumerPlan ? await getSubscribedPlanProductType() : undefined;
      if (subscribedPlanProductType) {
        setGraphQLPlansContext(currentContext => ({
          ...currentContext,
          [subscribedPlanProductType]: {
            subscriptionPlan: {},
            // Empty plan data for subscribed users
            isSubscriber: isSubscriberToAnyConsumerPlan,
            isSubscriberToSpecificPlan: true,
            isLoading: false
          }
        }));
      }
      const consumerPlanVariables = variables.filter(variable => {
        var _variable$filters;
        return ((_variable$filters = variable.filters) === null || _variable$filters === void 0 ? void 0 : _variable$filters.productType) && Array.from(CONSUMER_PRODUCT_TYPES).includes(variable.filters.productType);
      });
      const otherPlanVariables = variables.filter(variable => {
        var _variable$filters2;
        return ((_variable$filters2 = variable.filters) === null || _variable$filters2 === void 0 ? void 0 : _variable$filters2.productType) && !Array.from(CONSUMER_PRODUCT_TYPES).includes(variable.filters.productType);
      });
      if (consumerPlanVariables.length > 0) {
        let retrievedProductTypes = [];
        for (const queryVariables of consumerPlanVariables) {
          var _queryVariables$filte3, _queryVariables$filte4, _productTypes$map$fil;
          if (retrievedProductTypes.includes((_queryVariables$filte3 = (_queryVariables$filte4 = queryVariables.filters) === null || _queryVariables$filte4 === void 0 ? void 0 : _queryVariables$filte4.productType) !== null && _queryVariables$filte3 !== void 0 ? _queryVariables$filte3 : undefined)) {
            continue;
          }
          // Get all product types with matching query configurations.
          const productTypes = consumerPlanVariables.filter(otherQueryVariables => {
            return queryVariables.licenseCount === otherQueryVariables.licenseCount && queryVariables.marketingURL === otherQueryVariables.marketingURL && queryVariables.couponCode === otherQueryVariables.couponCode && queryVariables.excludeTrialOffers === otherQueryVariables.excludeTrialOffers;
          }).map(variable => {
            var _variable$filters3;
            return (_variable$filters3 = variable.filters) === null || _variable$filters3 === void 0 ? void 0 : _variable$filters3.productType;
          }).filter(productType => !!productType)
          // Don't overwrite the subscribed product state.
          .filter(productType => productType !== subscribedPlanProductType);
          if (productTypes.length < 1) {
            continue;
          }

          // Then retrieve all product types with the same query configuration in one API call.
          let consumerPlans;
          try {
            consumerPlans = await fetchGraphQLUpgradeableConsumerSubscriptionPlans({
              licenseCount: queryVariables.licenseCount,
              marketingURL: queryVariables.marketingURL,
              couponCode: queryVariables.couponCode,
              excludeTrialOffers: queryVariables.excludeTrialOffers
            });
          } catch (_error) {
            consumerPlans = [];
          }
          // Don't override the potential subscribed plan type
          const consumerPlanContext = Object.fromEntries((_productTypes$map$fil = productTypes.map(productType => {
            const graphQLPlan = consumerPlans.find(planData => planData.subscriptionPlan.productType === productType);
            // If a plan is unavailable, but the user is subscribed to some consumer plan, then set
            // the isSubscriber flag to true for backwards compatibility.
            if (!graphQLPlan && isSubscriberToAnyConsumerPlan) {
              return [productType, {
                subscriptionPlan: {},
                isSubscriber: true,
                isSubscriberToSpecificPlan: false,
                isLoading: false
              }];
            } else if (graphQLPlan) {
              return [productType, {
                ...graphQLPlan,
                isSubscriber: isSubscriberToAnyConsumerPlan
              }];
            } else {
              return [productType, {
                subscriptionPlan: {},
                isSubscriber: false,
                isSubscriberToSpecificPlan: false,
                isLoading: false
              }];
            }
          }).filter(entry => !!entry)) !== null && _productTypes$map$fil !== void 0 ? _productTypes$map$fil : []);
          setGraphQLPlansContext(currentContext => ({
            ...currentContext,
            ...consumerPlanContext
          }));
          retrievedProductTypes = [...retrievedProductTypes, ...productTypes];
        }
      }

      // Now query for non-consumer plans
      otherPlanVariables.forEach(queryVariable => {
        var _queryVariable$filter;
        const productType = (_queryVariable$filter = queryVariable.filters) === null || _queryVariable$filter === void 0 ? void 0 : _queryVariable$filter.productType;
        if (!productType) {
          return;
        }
        const fetchWrapper = async () => {
          try {
            const graphqlData = await fetchGraphQLSubscriptionPlan(queryVariable);
            setGraphQLPlansContext(currentContext => ({
              ...currentContext,
              [productType]: {
                ...graphqlData,
                isSubscriber: isSubscriberToAnyConsumerPlan
              }
            }));
          } catch (_error) {
            setGraphQLPlansContext(currentContext => ({
              ...currentContext,
              [productType]: {
                subscriptionPlan: {},
                isSubscriber: isSubscriberToAnyConsumerPlan,
                isSubscriberToSpecificPlan: subscribedPlanProductType === productType,
                isLoading: false
              }
            }));
          }
        };
        fetchWrapper();
      });
    })().finally(removeLoadingStates);
  }, [removeLoadingStates]);
  useImperativeHandle(ref, () => ({
    refetch: newVariables => {
      fetchGraphQLPlans(newVariables);
    }
  }));
  useEffect(() => {
    fetchGraphQLPlans(props.subscriptionPlanQueryVariables);
  }, [fetchGraphQLPlans, props.subscriptionPlanQueryVariables]);
  return /*#__PURE__*/React.createElement(GraphQLSubscriptionPlansContext.Provider, {
    value: graphqlPlansContext
  }, props.children);
});
//# sourceMappingURL=graphql-subscription-plan-provider.react-component.js.map